import { resolveChipColorFromThresholds } from './dashboardChips';

/**
 * Calcula estatísticas ambientais
 */
export function getEnvironmentalStats(data) {
  const properties = data?.properties || {};
  const car = properties.car || [];

  const stats = {
    hasCarThemes: false,
    themes: {},
    totalArea: 0,
    appArea: 0,
    rlArea: 0,
    hasAPP: false,
    hasRL: false,
    embargosCount: 0,
    debitosCount: 0,
  };

  if (Array.isArray(car)) {
    car.forEach((carItem) => {
      if (Array.isArray(carItem.themes)) {
        stats.hasCarThemes = true;

        carItem.themes.forEach((theme) => {
          const nome = theme.nome_tema || 'Sem nome';
          const area = parseFloat(theme.area) || 0;

          if (!stats.themes[nome]) {
            stats.themes[nome] = { count: 0, area: 0 };
          }
          stats.themes[nome].count++;
          stats.themes[nome].area += area;

          const nomeUpper = nome.toUpperCase();
          if (nomeUpper.includes('APP') || nomeUpper.includes('PRESERVAÇÃO PERMANENTE')) {
            stats.hasAPP = true;
            stats.appArea += area;
          }
          if (nomeUpper.includes('RESERVA LEGAL') || nomeUpper.includes('RL')) {
            stats.hasRL = true;
            stats.rlArea += area;
          }
          if (nomeUpper.includes('ÁREA') && nomeUpper.includes('IMÓVEL')) {
            stats.totalArea += area;
          }
        });
      }
    });
  }

  // Embargos IBAMA
  const embargos = data?.ibama_embargos || properties.ibama_embargos || [];
  stats.embargosCount = Array.isArray(embargos) ? embargos.length : 0;

  // Débitos IBAMA
  const debitos = data?.ibama_debitos || properties.ibama_debitos || [];
  stats.debitosCount = Array.isArray(debitos) ? debitos.length : 0;

  return stats;
}

// Helper para classificar a situação de Reserva Legal (RL)
// Regra extraída de CarDomain.classifyReservaLegal para uso compartilhado (ex: Dashboard)
const safeNumberReservaLegal = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const getDominantBiomaInfoForReservaLegal = (sobreposicaoData) => {
  if (!sobreposicaoData) return null;

  const array = Array.isArray(sobreposicaoData) ? sobreposicaoData : [sobreposicaoData];

  let dominant = null;

  array.forEach((item) => {
    const biomas = Array.isArray(item?.temas?.biomas) ? item.temas.biomas : [];
    biomas.forEach((b) => {
      const nome = b?.bioma;
      const pct = safeNumberReservaLegal(b?.pct_imovel);
      if (!nome || pct === null) return;
      if (!dominant || pct > dominant.pct) {
        dominant = { bioma: nome, pct };
      }
    });
  });

  return dominant;
};

export function classifyReservaLegalStatus(details, sobreposicaoData) {
  const areaLiquida = safeNumberReservaLegal(details?.area_liquida);
  if (areaLiquida === null || areaLiquida === 0) {
    return { label: 'N/D', color: 'default' };
  }

  const aprovNaoAverb = safeNumberReservaLegal(
    details?.area_de_reserva_legal_aprovada_e_nao_averbada,
  );
  const averbada = safeNumberReservaLegal(details?.area_de_reserva_legal_averbada);
  const proposta = safeNumberReservaLegal(details?.area_de_reserva_legal_proposta);

  let arlArea = null;

  if (aprovNaoAverb !== null && aprovNaoAverb > 0) {
    arlArea = aprovNaoAverb;
  } else if (averbada !== null && averbada > 0) {
    arlArea = averbada;
  } else if (proposta !== null && proposta > 0) {
    arlArea = proposta;
  }

  if (arlArea === null) {
    return { label: 'N/D', color: 'default' };
  }

  const arlPercent = (arlArea / areaLiquida) * 100;

  const dominantBioma = getDominantBiomaInfoForReservaLegal(sobreposicaoData);
  let limite = 20;

  if (dominantBioma?.bioma) {
    const upper = String(dominantBioma.bioma).toUpperCase();
    if (upper.includes('AMAZONIA') || upper.includes('AMAZÔNIA')) {
      limite = 80;
    } else if (upper.includes('CERRADO')) {
      limite = 35;
    }
  }

  const epsilon = 0.0001;
  let label = 'Regular';
  let color = 'default';

  if (arlPercent + epsilon < limite) {
    label = 'Déficit de Reserva Legal';
    color = 'error';
  } else if (Math.abs(arlPercent - limite) <= epsilon) {
    label = 'Regular';
    color = 'default';
  } else if (arlPercent - epsilon > limite) {
    label = 'Excedente de Reserva Legal';
    color = 'success';
  }

  return { label, color };
}

export function getCarReservaLegalDashboardStatus(data) {
  const properties = data?.properties || {};
  const carRaw = properties.car;

  const carArray = Array.isArray(carRaw) ? carRaw : carRaw ? [carRaw] : [];

  const label = 'Reserva Legal';

  if (carArray.length === 0) {
    return { label, color: 'error' };
  }

  const sobreposicao = properties.sobreposicao;

  const statusValues = [];
  const rlLabels = [];
  const rlColors = [];

  carArray.forEach((carItem) => {
    const details = carItem?.details || {};
    const statusCode = details.situacao_do_cadastro;
    statusValues.push(statusCode == null || statusCode === '' ? null : String(statusCode));

    const rlStatus = classifyReservaLegalStatus(details, sobreposicao);
    rlLabels.push(rlStatus.label || null);
    rlColors.push(rlStatus.color || 'default');
  });

  const normalize = (value) => (value == null ? '__NULL__' : String(value));

  const uniqueStatus = Array.from(new Set(statusValues.map(normalize)));
  const uniqueRlLabels = Array.from(new Set(rlLabels.map(normalize)));

  const hasStatusDivergence = uniqueStatus.length > 1;
  const hasRlDivergence = uniqueRlLabels.length > 1;

  if (hasStatusDivergence || hasRlDivergence) {
    return { label, color: 'warning' };
  }

  const baseColor = rlColors[0] || 'default';
  return { label, color: baseColor };
}

export function getPraDashboardStatus(data, dashboardChipsConfig) {
  const properties = data?.properties || {};
  const carRaw = properties.car;

  const carArray = Array.isArray(carRaw) ? carRaw : carRaw ? [carRaw] : [];

  let praCount = 0;

  carArray.forEach((carItem) => {
    const details = carItem?.details || {};
    const value = details.solicitacao_de_adesao_ao_pra;
    if (value !== null && value !== undefined && value !== '') {
      const normalized = String(value).toLowerCase();
      if (normalized === 'sim') {
        praCount += 1;
      }
    }
  });

  const label = 'PRA';
  let color = praCount === 0 ? 'success' : 'warning';

  const chipsConfig = dashboardChipsConfig || {};
  const ambientalCfg = chipsConfig.ambiental || {};
  const praCfg = ambientalCfg.pra;

  if (praCfg && Array.isArray(praCfg.thresholds)) {
    color = resolveChipColorFromThresholds(praCfg, praCount, color) || color;
  }

  return { label, color };
}

export function getEmbargosDashboardStatus(data, dashboardChipsConfig) {
  const properties = data?.properties || {};

  const rootEmbargos = Array.isArray(data?.ibama_embargos)
    ? data.ibama_embargos
    : data?.ibama_embargos
      ? [data.ibama_embargos]
      : null;

  const propEmbargos = Array.isArray(properties.ibama_embargos)
    ? properties.ibama_embargos
    : properties.ibama_embargos
      ? [properties.ibama_embargos]
      : null;

  const embargosArray = rootEmbargos || propEmbargos || [];

  let embargosCount = 0;

  embargosArray.forEach((item) => {
    const retorno = item?.retorno || {};
    const possuiEmbargo = retorno.possuiEmbargo === true;
    const listaEmbargos = retorno.embargos;
    const hasListaEmbargos = Array.isArray(listaEmbargos) && listaEmbargos.length > 0;

    if (possuiEmbargo || hasListaEmbargos) {
      embargosCount += 1;
    }
  });

  const label = 'Embargos';
  let color = embargosCount === 0 ? 'success' : 'warning';

  const chipsConfig = dashboardChipsConfig || {};
  const ambientalCfg = chipsConfig.ambiental || {};
  const embargosCfg = ambientalCfg.embargos;

  if (embargosCfg && Array.isArray(embargosCfg.thresholds)) {
    color = resolveChipColorFromThresholds(embargosCfg, embargosCount, color) || color;
  }

  return { label, color };
}
